"use client";

import { useEffect } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import {
  identifyUser,
  trackRegister,
  registerSuperProperties,
  setPersonProperties,
} from "@/lib/analytics";

function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari reports via navigator.standalone
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

export function PostHogIdentify({
  userId,
  email,
  tier,
}: {
  userId: string;
  email?: string;
  tier?: string;
}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const isNewUser = searchParams.get("new_user") === "1";

  // Identify + register super-properties + set PWA person property.
  // Depends only on identity-relevant inputs so it doesn't re-fire on every
  // in-app navigation (which would spam PostHog's $set person endpoint).
  useEffect(() => {
    const props: Record<string, unknown> = {};
    if (email) props.email = email;
    if (tier) props.tier = tier;

    const isPwa = detectStandalone();
    registerSuperProperties({
      display_mode: isPwa ? "standalone" : "browser",
      is_pwa: isPwa,
    });
    if (isPwa) {
      setPersonProperties({ has_used_pwa: true });
    }

    identifyUser(userId, Object.keys(props).length > 0 ? props : undefined);
  }, [userId, email, tier]);

  // Fire the register conversion once per new signup and strip the query flag.
  useEffect(() => {
    if (!isNewUser || !email) return;
    trackRegister(email);

    const params = new URLSearchParams(searchParams.toString());
    params.delete("new_user");
    const qs = params.toString();
    router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
  }, [isNewUser, email, searchParams, router, pathname]);

  return null;
}
